import {
  HomepageCollectionComponentType,
  MasterHomepageRowStructureType,
} from '@/graphql/types/generated';

const MASTER_HOMEPAGE_ROW_TYPE_LABELS: Record<string, string> = {
  [MasterHomepageRowStructureType.CollectionBanner]: 'Banner Half Panel',
  [MasterHomepageRowStructureType.CollectionCarousel]: 'Item Carousel',
  [MasterHomepageRowStructureType.CollectionHighlightCarousel]:
    'Banner Carousel',
  [MasterHomepageRowStructureType.NewItemsCarousel]: 'New Items Carousel',
  [MasterHomepageRowStructureType.TrendingItemsCarousel]:
    'Trending Items Carousel',
};

/**
 * What `ImageUpload` puts in a form value between picking a file and the submit
 * that uploads it. It is never a storage key, so anything mapping a form value
 * to a saved input has to read it as empty rather than send it on.
 */
export const PENDING_UPLOAD = 'pending-upload';

export const POSITIONS = [
  { key: '1A', label: 'Row 1A', type: 'Banner Half Panel' },
  { key: '1B', label: 'Row 1B', type: 'Banner Half Panel' },
  { key: '2', label: 'Row 2', type: 'Carousel' },
  { key: '3', label: 'Row 3', type: 'Highlight Carousel' },
  { key: '4', label: 'Row 4', type: 'Carousel' },
  { key: '5', label: 'Row 5', type: 'Highlight Carousel' },
  { key: '6', label: 'Row 6', type: 'Carousel' },
  { key: '7', label: 'Row 7', type: 'Highlight Carousel' },
] as const;

export type Position = typeof POSITIONS[number]['key'];

export const formatMarkets = (
  warehouses: { id: string; name: string }[],
  totalWarehouses: number,
): string => {
  const count = warehouses.length;
  const base = `(${count}/${totalWarehouses} Markets)`;
  if (count > 0 && count < totalWarehouses) {
    return `${base} ${warehouses.map((w) => w.name).join(', ')}`;
  }
  return base;
};

export const formatComponentType = (
  componentType: HomepageCollectionComponentType,
): string => {
  switch (componentType) {
    case HomepageCollectionComponentType.CollectionBanner:
      return 'Half-Banner';
    case HomepageCollectionComponentType.CollectionHighlightCarousel:
      return 'Highlight Carousel';
    case HomepageCollectionComponentType.CollectionCarousel:
      return 'Carousel';
    case HomepageCollectionComponentType.CollectionPromoPage:
      return 'Promo Page';
    default:
      return componentType;
  }
};

export const formatDateRange = (
  startDate: Date | string,
  endDate: Date | string,
): string => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  const formatDate = (date: Date) => {
    // Adjust for timezone offset to prevent off-by-one-day errors
    // ISO dates are parsed as UTC, but we want to display in local date
    const adjustedDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60000,
    );
    return adjustedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
};

export function todayString() {
  return new Date().toISOString().split('T')[0];
}

export function getHomepageComponentTypeForRow(
  structureType: MasterHomepageRowStructureType,
): HomepageCollectionComponentType | null {
  switch (structureType) {
    case MasterHomepageRowStructureType.CollectionBanner:
      return HomepageCollectionComponentType.CollectionBanner;
    case MasterHomepageRowStructureType.CollectionCarousel:
      return HomepageCollectionComponentType.CollectionCarousel;
    case MasterHomepageRowStructureType.CollectionHighlightCarousel:
      return HomepageCollectionComponentType.CollectionHighlightCarousel;
    default:
      return null;
  }
}

export function getMasterHomepageBannerSide(sortOrder?: number | null) {
  switch (sortOrder) {
    case 1:
      return 'A';
    case 2:
      return 'B';
    default:
      return '';
  }
}

export function formatMasterHomepageRowType(
  structureType: MasterHomepageRowStructureType,
) {
  return MASTER_HOMEPAGE_ROW_TYPE_LABELS[structureType] ?? structureType;
}
